import { useModal } from "@/context/ModalStackManager";

import { PalletBarcodeForm } from "@/components/organism/form/PalletBarcodeForm";
import { RawMaterialForm } from "@/components/organism/form/RawMaterialForm";
import { DefectObject } from "@/components/organism/work/original/WorkCurrentDefectInform";
import { customFunctions } from "@/config/customFunctions";
import { useLocalStorage } from "@/context/LocalStorageProvider";
import { useWorkLotsFetch } from "@/fetch/page/work/useWorkLotsFetch";
import { useBarcodePrintHandler } from "@/hooks/handler/useBarcodePrintHandler";
import { useInventoryHandler } from "@/hooks/handler/useInventoryHandler";
import { useStatusHandler } from "@/hooks/handler/useStatusHandler";
import { useNotification } from "@/hooks/useNotification";
import { usePerformance } from "@/hooks/usePerformance";
import i18n from "@/language/i18n";
import { useEquipmentStore } from "@/store/equipment.store";
import { useSocketStore } from "@/store/socket.store";
import { useWorkDataStore } from "@/store/work.store";
import { customNotification } from "@/utils/notificationShow";
import {
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksGroupDefectPutRequest,
  WorksGroupFakeDefectPutRequest,
  WorksGroupPerformancePutRequest,
  WorksWorkIdDefectPutRequest,
  WorksWorkIdPerformancePutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import BigNumber from "bignumber.js";
import { t } from "i18next";

export const usePerformanceHandler = () => {
  const { tempWork, setTempWork } = useLocalStorage();
  const { openModal, closeModal } = useModal();
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));
  const { equipment } = useEquipmentStore((state) => ({
    equipment: state.Equipment,
  }));
  const { getData } = useSocketStore((state) => ({ getData: state.getData }));
  const counter = getData(equipment?.code as string);

  const { work } = workData;
  const { data } = useWorkLotsFetch({
    workData: work as ProductionPlansGet200ResponseRowsInnerWorksInner,
  });
  const lots = data?.data;
  const locationCode = work?.locationSetting?.targetLocation?.code;

  const {
    onPerformance,
    defectMutateAsync,
    onDefect,
    onGroupPerformance,
    onGroupDefect,
    onGroupFakeDefect,
  } = usePerformance();

  const { handlePreviousOrderClosure, handleEnd } = useStatusHandler();
  const { notifyLimitHitModal, notifyOutOfStockModal } = useNotification();
  const { handleFlush } = useInventoryHandler();

  const { onSubmit: onBarcodePrint } = useBarcodePrintHandler();

  const currentTempDefectList = Object.keys(
    tempWork?.defects ? (tempWork?.defects as DefectObject) : {}
  ).map((code) => {
    return {
      defectName: (tempWork?.defects as DefectObject)[code]
        ?.defectName as string,
      defectCode: code,
      defectQuantity: (tempWork?.defects as DefectObject)[code]
        ?.defectQuantity as string,
      setUnitText: work?.item?.unitText,
    };
  });

  const currentTempDefectTotal = currentTempDefectList.reduce(
    (acc, cur) =>
      BigNumber(acc)
        .plus(cur.defectQuantity as string)
        .toString(),
    "0"
  );

  const validateDefectQuantities = () => {
    if (!tempWork?.defects) return false;

    for (const code of Object.keys(tempWork.defects)) {
      if (tempWork.defects[code]?.defectQuantity === "0") continue;

      const currentLotDefectQuantity = (
        work?.currentLotSummary?.defect as DefectObject
      )?.[code]?.defectQuantity as string;

      const tempDefectQuantity = tempWork.defects[code]
        ?.defectQuantity as string;

      if (
        BigNumber(currentLotDefectQuantity).isGreaterThanOrEqualTo(
          tempDefectQuantity
        )
      ) {
        const defectName = (work?.currentLotSummary?.defect as DefectObject)?.[
          code
        ].defectName;
        customNotification.error({
          message: `${defectName} ${t(
            "의 임시 수량이 현재 로트의 불량 수량보다 적습니다."
          )}`,
        });
        return true;
      }
    }
    return false;
  };

  // 실적입력 유효성 검사
  const validatePerformance = (quantity: string) => {
    const totalEnd = BigNumber(work?.currentLotSummary?.end ?? "0").plus(
      quantity
    );
    const totalDefects = BigNumber(currentTempDefectTotal).plus(
      work?.currentLotSummary?.defectTotal ?? "0"
    );
    const isDefectsGreaterThanEnd = totalDefects.isGreaterThan(totalEnd);
    const isQuantityLessThanTotal = BigNumber(quantity).isLessThanOrEqualTo(
      work?.currentLotSummary?.totalQuantity as string
    );
    const isValidDefectQuantity = validateDefectQuantities();

    if (isDefectsGreaterThanEnd) {
      customNotification.error({
        message: t("불량 수량이 입력한 실적 수량보다 많습니다."),
      });
      return false;
    }

    if (isQuantityLessThanTotal) {
      customNotification.error({
        message: t("생산 수량이 입력한 실적 수량보다 같거나 많습니다."),
      });
      return false;
    }

    if (isValidDefectQuantity) return false;

    return true;
  };

  // 임시 불량 저장
  const handleTempDefectSave = async () => {
    for (const defect of currentTempDefectList) {
      if (defect.defectQuantity === "0") continue;
      try {
        await defectMutateAsync(
          {
            workId: work?.id as number,
            worksWorkIdDefectPutRequest: {
              defectCode: defect.defectCode + "",
              accQuantity: BigNumber(
                defect.defectQuantity as string
              ).toString(),
              lotId: lots?.id,
            },
          },
          {
            onSuccess: () => {
              setTempWork({
                ...tempWork,
                defects: {},
              });
            },
          }
        );
      } catch (error: any) {
        customNotification.error({
          message:
            error?.response?.data?.message ?? t("불량 저장에 실패했습니다."),
        });
        return;
      }
    }
  };

  /**
   * ✅ 변경 핵심: handlePerformance가 절대 throw하지 않고 boolean을 반환
   *  - 성공: true
   *  - 실패/재시도 실패: false
   */
  const handlePerformance = async (
    options: WorksWorkIdPerformancePutRequest
  ): Promise<boolean> => {
    const handleOnSuccess = () => {
      if (currentTempDefectList.length > 0) {
        handleTempDefectSave().catch(() => {});
      }
      closeModal({});
    };

    const handleOnError = async (e: any): Promise<boolean> => {
      const errorMessage = e?.response?.data?.message;

      const tryRetry = async () => {
        try {
          await onPerformance(
            { ...options, warnInsufficiency: false, priorityConsume: true },
            { onSuccess: handleOnSuccess }
          );
          return true;
        } catch (err) {
          return false;
        }
      };

      const errorHandlers: Record<string, () => Promise<boolean> | boolean> = {
        "재고가 부족합니다.": async () => {
          const isWireRod = Boolean(
            work?.item?.name?.includes("선재(반제품)1")
          );

          if (isWireRod) return false;

          const confirmOutOfStock = await notifyOutOfStockModal();
          if (confirmOutOfStock) {
            await openModal(<RawMaterialForm />, "", t("원부자재 투입"));
          } else if (customFunctions.ADD_SAVE_NO_MINUS_PERFORMANCE) {
            return false;
          }
          return await tryRetry();
        },

        "accQuantity is less than current accQuantity": () => {
          customNotification.error({
            message: t("이전 생산수량보다 큰 수량을 입력해주세요."),
          });
          return false;
        },
        "Quantity must be positive": () => {
          customNotification.error({
            message: t("이전 생산수량보다 큰 수량을 입력해주세요."),
          });
          return false;
        },
        "accQuantity 값이 현재 현재 accQuantity값보다 작습니다.": () => {
          customNotification.error({
            message: t("이전 생산수량보다 큰 수량을 입력해주세요."),
          });
          return false;
        },
      };

      if (errorMessage && errorHandlers[errorMessage]) {
        return await errorHandlers[errorMessage]();
      } else {
        customNotification.error({
          message: errorMessage ?? t("실적 저장에 실패했습니다."),
        });
        return false;
      }
    };

    try {
      await onPerformance(options, { onSuccess: handleOnSuccess });
      return true;
    } catch (e) {
      return await handleOnError(e);
    }
  };

  // 실적입력 전체 흐름(유효성/플러시/실행/후속처리)
  const handlePerformanceWithValidation = async (
    quantity: string
  ): Promise<boolean> => {
    const isWireRod = Boolean(work?.item?.name?.includes("선재(반제품)1"));

    if (!customFunctions.ADD_SKIP_PREVIOUS_WORK_ORDER_CONFIRM) {
      const previousOrderClosure = await handlePreviousOrderClosure();
      if (!previousOrderClosure) return false;
    }

    if (customFunctions.ADD_TEMP_FUNCTION_ENABLED) {
      const isValid = validatePerformance(quantity);
      if (!isValid) return false;
    }

    if (customFunctions.ADD_FLUSHING) {
      await handleFlush();
    }

    const isPerformance = await handlePerformance(
      customFunctions.ADD_ACCUMULATION_QUANTITY
        ? {
            accQuantity: quantity,
            warnInsufficiency: customFunctions.ADD_WARN_INSUFFICIENCY,
            priorityConsume: true,
          }
        : {
            quantity: quantity,
            warnInsufficiency: customFunctions.ADD_WARN_INSUFFICIENCY,
            priorityConsume: true,
          }
    );

    if (!isPerformance) return false;

    if (isWireRod) {
      const result = await handleEnd({
        workId: work?.id as number,
        worksWorkIdTrackingStartPostRequest: {
          workId: work?.id as number,
        },
        useCloseModal: false,
      });

      const lotName = result?.data.workLog?.lotInfo?.lotName;
      if (lotName) {
        await onBarcodePrint({
          lotName: [lotName as string],
          locationCode: [locationCode as string],
          itemCode: [work?.item?.code as string],
        });
      }
    }
    return true;
  };

  // 파레트 분리
  const handlePalletSeparate = async () => {
    return openModal(<PalletBarcodeForm />, "", i18n.t("투입 기록 확인"));
  };

  // 리밋 카운트 도달 처리
  const handleLimitCounter = async () => {
    const confirmLimitHit = await notifyLimitHitModal({
      unitText: work?.item?.unitText,
      equipmentLimitCounter: counter?.equipmentLimitCounter,
      defectTotal: currentTempDefectTotal,
    });

    if (confirmLimitHit) {
      await handlePerformanceWithValidation(
        counter?.equipmentLimitCounter as string
      );
    }
  };

  const handleOnDefect = async (defectRequest: WorksWorkIdDefectPutRequest) => {
    onDefect(defectRequest);
  };

  const handleGroupPerformance = async (
    groupPerformanceRequest: WorksGroupPerformancePutRequest
  ) => {
    onGroupPerformance(groupPerformanceRequest);
  };

  const handleGroupDefect = async (
    defectRequest: WorksGroupDefectPutRequest
  ) => {
    onGroupDefect(defectRequest);
  };

  const handleGroupFakeDefect = async (
    defectFakeRequest: WorksGroupFakeDefectPutRequest
  ) => {
    onGroupFakeDefect(defectFakeRequest);
  };

  return {
    handlePerformanceWithValidation,
    handlePerformance,
    handleTempDefectSave,
    handleLimitCounter,
    handlePalletSeparate,
    handleOnDefect,
    handleGroupPerformance,
    handleGroupDefect,
    handleGroupFakeDefect,
  };
};
